import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { API } from '@unboared/react';

import { db } from '~/config/firebase.config';
import { excludeGamesForClient, isFreePlayClient } from '~/config/airports.config';
import { FREEPLAY_GAME_IDS } from '~/config/global.config';
import { Mode } from '~/types/client';
import { useGame } from '~/hooks/models/useGame';
import { useSyncClient } from '~/hooks/models/useClient';
import { useRandomGameReload } from '~/hooks/useRandomGameReload';
import { useScreenPlayerCount } from '~/hooks/useScreenPlayerCount';
import { normalizeScreenID } from '~/utils/screenID';
import { useFreePlayHostOrchestrator, useFreePlayState } from '~/hooks/freePlay';
import { FreePlayIdleScreen, FreePlayTransition, VoteBanner, VoteResultBanner } from '~/components/freePlay';
import { FP_DEFAULTS, VoteContext, VoteOutcome } from '~/services/freePlay/types';
import { useDataCollector } from '~/hooks/useDataCollector';
import { FirestoreScreenDataCollector } from '~/types/data_collector/FirestoreScreenDataCollector';
import { FirestoreGamepadDataCollector } from '~/types/data_collector/FirestoreGamepadDataCollector';
import { FirestorePlayerVisitDataCollector } from '~/types/data_collector/FirestorePlayerVisitDataCollector';

import { GameRunner, GameRunnerVersion } from '~/components/GameRunner';

import { ThreeSideLayout } from '~/components/layouts/ThreeSideLayout';
import { TwoSideLayout } from '~/components/layouts/TwoSideLayout';
import { Layout16x9 } from '~/components/layouts/Layout16x9';
import { FullscreenLayout } from '~/components/layouts/FullscreenLayout';

import { LoadingPage } from '../LoadingPage';
import { useLimitedAccess } from './useLimitedAccess';
//

/** No-op data collector — used while no game is active (free-play idle) so we don't
    write phantom stats sessions with an empty gameID. */
const NULL_DATA_COLLECTOR = { start() {}, stop() {} };

/** Fetches the proposed game and renders the TV vote banner. Mounted only while a vote is active. */
function ActiveVoteBanner({ vote }: { vote: VoteContext }) {
    const proposedGame = useGame(vote.proposedGameID);
    return <VoteBanner vote={vote} proposedGame={proposedGame} />;
}

/** Fetches the proposed game and renders the TV result banner after a vote settles. */
function ActiveVoteResultBanner({ outcome }: { outcome: VoteOutcome }) {
    const proposedGame = useGame(outcome.proposedGameID);
    return <VoteResultBanner outcome={outcome} proposedGame={proposedGame} />;
}

export type ScreenForInstanceProps = {
    clientID: string;
    screenID?: string;
    gameID: string;
};

export function ScreenForInstance({ clientID, screenID, gameID }: ScreenForInstanceProps) {
    useLimitedAccess(clientID);
    const { t } = useTranslation();

    const isAnimMode = !screenID;
    const client = useSyncClient(clientID);
    const freePlayEnabled = isFreePlayClient(client);

    // Free-play: the host orchestrator owns the active game (a gamepad picks a
    // game from idle, or a vote validates a switch). The shared `gameID` property
    // below then drives the gamepads' generic game-follow pipeline (useCurrentGameID)
    // — no free-play-specific iframe juggling needed.
    useFreePlayHostOrchestrator({ enabled: freePlayEnabled });
    const freePlay = useFreePlayState();
    // In free-play we use ONLY the orchestrator's game; NO fallback to the URL gameID.
    // While idle (currentGameID null) the screen must run no game at all — otherwise
    // the default game (e.g. geoloc) would play in the background and the next picked
    // game would be joined mid-progress. Idle is handled by the early return below.
    const effectiveGameID = freePlayEnabled ? freePlay.currentGameID : gameID;
    const isFreePlayIdle = freePlayEnabled && !effectiveGameID;

    // Free-play catalog for this client, minus its excluded games (e.g. footboared
    // at Orly). Memoized on clientID so the array reference stays stable across
    // re-renders — an unstable ref here would re-trigger useGames' fetch on every
    // parent render (unstable-refs gotcha).
    const freePlayCatalog = useMemo(() => excludeGamesForClient(FREEPLAY_GAME_IDS, clientID), [clientID]);

    // Attract-screen languages for THIS screen (Firestore `screenInfos.{id}.idleLanguages`,
    // read live like every other per-screen setting). Lets one screen speak its gate's
    // audience — e.g. ['zh','en'] on Marseille's Africa/Asia transfer lounge — without
    // touching the client's other screens. Absent → undefined → the idle screen keeps
    // its FR/EN default. No memo needed: FreePlayIdleScreen normalises this to a
    // primitive key internally, so a fresh Firestore snapshot with identical languages
    // doesn't re-run anything downstream.
    const idleLanguages = client?.getScreen(normalizeScreenID(screenID))?.idleLanguages;

    // Auto-rotation is replaced by free-play; disable the timed reload when active.
    useRandomGameReload(!isAnimMode && !freePlayEnabled, effectiveGameID ?? '', clientID);

    // Publish the live connected-player count to Firestore for the admin live
    // dashboard. Key normalized exactly like the data collectors (anim/bar → 'anim',
    // gotcha #100 guard). Covers the free-play lobby too.
    useScreenPlayerCount(clientID, normalizeScreenID(screenID));

    // Read game.screenURL directly. A previous intermediate `screenURL` state
    // (updated via an effect) only ever held the LAST non-empty URL, which froze
    // the iframe on the previous game during fast free-play switches and made the
    // out-of-order `useData` race (gotcha #86) visible on screen. The iframe `src`
    // must follow `game` exactly, so drive it from `game.screenURL`.
    const game = useGame(effectiveGameID ?? '');

    // --- Free-play game-switch transition (Phase 4) --------------------------
    // Cover the iframe reload between two games with a cinematic "now launching"
    // curtain, removed at the EXACT moment the new game's iframe completes its
    // handshake (onReady) rather than on a blind timer — so the traveller never
    // sees a blank/flashing frame during a switch (bug #8b). Free-play only;
    // bars/anim never mount this.
    const [transitionFor, setTransitionFor] = useState<string | null>(null);
    const [readyGameID, setReadyGameID] = useState<string | null>(null);

    // The game actually loaded in the iframe, read inside the iframe's
    // once-registered onReady handler (avoids a stale closure).
    const loadedGameRef = useRef<string | undefined>(undefined);
    loadedGameRef.current = game?.id;

    // Raise the curtain on every game change (and the first pick) while free-play
    // is on. The new game's onReady then lowers it (or the safety net below).
    useEffect(() => {
        setTransitionFor(freePlayEnabled && effectiveGameID ? effectiveGameID : null);
    }, [freePlayEnabled, effectiveGameID]);

    const handleGameReady = useCallback(() => {
        setReadyGameID(loadedGameRef.current ?? null);
    }, []);

    const transitionReady = transitionFor != null && readyGameID === transitionFor;

    // Safety net: never let the curtain block the game forever if onReady never
    // arrives (legacy runner, stuck game). Reveal after a bounded wait.
    useEffect(() => {
        if (!transitionFor || transitionReady) return undefined;
        const timer = window.setTimeout(() => setReadyGameID(transitionFor), 4500);
        return () => window.clearTimeout(timer);
    }, [transitionFor, transitionReady]);

    // Only collect stats when a game is actually active. During free-play idle
    // effectiveGameID is null → use a no-op collector to avoid phantom sessions
    // with an empty gameID polluting the dashboards.
    useDataCollector(
        effectiveGameID
            ? new FirestoreScreenDataCollector(db, clientID, effectiveGameID, screenID || Mode.Anim)
            : NULL_DATA_COLLECTOR,
        isAnimMode,
        [effectiveGameID]
    );
    useDataCollector(
        effectiveGameID
            ? new FirestoreGamepadDataCollector(db, clientID, effectiveGameID, screenID || Mode.Anim)
            : NULL_DATA_COLLECTOR,
        isAnimMode,
        [effectiveGameID]
    );

    // Visit-level collector (free-play / airports only): one doc per player per VISIT,
    // spanning game switches — distinct from the two per-game collectors above. Mounted
    // ONCE per screen session (keyed by freePlayEnabled/client/screen, NOT by gameID), so
    // a player who goes A→B→C produces a SINGLE visit covering all of them. The instance
    // is stable across switches (useMemo) and fed the active game via noteGame so each
    // visit records its games. See FirestorePlayerVisitDataCollector.
    const visitCollector = useMemo(
        () => (freePlayEnabled ? new FirestorePlayerVisitDataCollector(db, clientID, screenID || Mode.Anim) : null),
        [freePlayEnabled, clientID, screenID]
    );
    useDataCollector(visitCollector ?? NULL_DATA_COLLECTOR, isAnimMode, [visitCollector]);
    useEffect(() => {
        visitCollector?.noteGame(effectiveGameID ?? null);
    }, [visitCollector, effectiveGameID]);

    useEffect(() => {
        API.loadSharedProperty(API.getHostID(), 'gameID', effectiveGameID);
    }, [effectiveGameID]);

    /* Persist the active gameID in the URL so a screen reload mid-session resumes
       on the right game (free-play state itself is in-memory only). Preserves the
       other params (notably the `srv` socket-server token) by cloning prev. */
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (!freePlayEnabled || !effectiveGameID) return;
        if (searchParams.get('game') === effectiveGameID) return;
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('game', effectiveGameID);
                return next;
            },
            { replace: true }
        );
    }, [freePlayEnabled, effectiveGameID, searchParams, setSearchParams]);

    // Leaving the active game (animator back to the selection menu, or screen
    // closing) → tell gamepads no game is active so they can show the inter-game
    // waiting / rating screen instead of freezing on the previous controller.
    // [] deps → runs on real unmount. In anim mode that's the menu return; in
    // auto mode each rotation is a window.location.reload() (also an unmount), but
    // that only fires when 0 gamepads are connected (see useRandomGameReload), so
    // no connected player is flipped to the rating screen mid-rotation.
    // The broadcast reaches the gamepads because the socket outlives this
    // cleanup in every case that matters: on a menu return only THIS component
    // unmounts (UnboaredGame stays mounted, socket live), and the console's API
    // is caller-owned (passed to UnboaredGame via the `api` prop, created once
    // per page in ScreenApp) so the lib's unmount cleanup (lib 0.9.5 close())
    // never closes it mid-page. On a full page unload nothing is guaranteed —
    // that's what the try/catch is for. NOTE: do NOT rely on unmount ORDER
    // between this component and UnboaredGame (React tears down a deleted tree
    // parent-first, not children-first as this comment previously claimed).
    useEffect(
        () => () => {
            try {
                API.loadSharedProperty(API.getHostID(), 'gameID', null);
            } catch {
                /* socket already gone (page unload) — best effort */
            }
        },
        []
    );

    // Coupon routing is now sent directly from coupon creation logic

    // Free-play idle: no game picked yet. Render the attract screen and — crucially —
    // NO GameRunner, so the next picked game starts fresh. The big QR selection
    // overlay (QRCodeButton auto-open, mounted in ScreenApp) sits on top.
    if (isFreePlayIdle) {
        return <FreePlayIdleScreen gameIDs={freePlayCatalog} languages={idleLanguages} />;
    }

    // The launch curtain. Keyed by game so each switch gets a fresh entrance; shown
    // over BOTH the loading state (first pick, while metadata resolves) and the live
    // game (until its iframe handshake completes). game?.id guard avoids flashing the
    // previous game's cover before the new metadata lands.
    const transitionOverlay = freePlayEnabled && transitionFor && (
        <FreePlayTransition
            key={transitionFor}
            gameID={transitionFor}
            game={game?.id === transitionFor ? game : undefined}
            ready={transitionReady}
            onDone={() => setTransitionFor(null)}
        />
    );

    if (!game || !game.screenURL) {
        return (
            <>
                <LoadingPage>
                    {effectiveGameID} : {t('console.loadingInfos')}
                </LoadingPage>
                {transitionOverlay}
            </>
        );
    }

    let Layout;
    switch (game.format) {
        case 'fullscreen':
            Layout = FullscreenLayout;
            break;
        case '16:9-down':
            Layout = Layout16x9;
            break;
        case '16:9-top':
        case '16:9':
            Layout = Layout16x9;
            break;
        case '13:9':
            Layout = TwoSideLayout;
            break;
        case '1:1':
            Layout = ThreeSideLayout;
            break;
        default:
            Layout = TwoSideLayout;
            break;
    }

    return (
        <>
            <Layout game={game} clientID={clientID}>
                {/* <GameRunner title={game.name} src="http://192.168.1.90:5174/" version="latest" /> */}
                <GameRunner
                    title={game.name}
                    src={game.screenURL}
                    version={(game.ubVersion as GameRunnerVersion) || 'latest'}
                    clientID={clientID}
                    onReady={handleGameReady}
                />
            </Layout>
            {transitionOverlay}
            {freePlayEnabled && freePlay.phase === 'voting' && freePlay.vote && (
                <ActiveVoteBanner vote={freePlay.vote} />
            )}
            {freePlayEnabled &&
                freePlay.phase === 'playing' &&
                freePlay.lastVoteOutcome &&
                Date.now() - freePlay.lastVoteOutcome.at < FP_DEFAULTS.voteResultMs && (
                    <ActiveVoteResultBanner outcome={freePlay.lastVoteOutcome} />
                )}
        </>
    );
}
